/**
 * Lockerer Contact-Typ — viele Aufrufer haben unterschiedliche Shapes (GraphQL,
 * REST, Search-Result). Wir lesen mit best-effort und bauen einen sinnvollen
 * Anzeigenamen. Liefert `null` wenn überhaupt nichts brauchbares da ist.
 */
type AnyContact = {
  firmenname?: string | null;
  companyName?: string | null;
  Firmenname?: string | null;
  vorname?: string | null;
  firstName?: string | null;
  Vorname?: string | null;
  nachname?: string | null;
  lastname?: string | null;
  lastName?: string | null;
  Nachname?: string | null;
  displayName?: string | null;
  Anzeigename?: string | null;
  name?: string | null;
  /** Fortsetzung eines abgeschnittenen Firmennamens — nur bei Firmen relevant. */
  suffix?: string | null;
  /**
   * Kontakttyp, sofern der Aufrufer ihn mitliefert (Firma/Person).
   * `number` ist mitgetypt, weil `ContactDetailsFormValues.typ` auch die
   * numerische Typ-Id tragen kann — `isFirma` normalisiert ueber `String(...)`.
   */
  typ?: string | number | null;
  type?: string | { type?: string | null } | null;
} | null | undefined;

/** Ist der Kontakt eine Firma? Fehlt die Angabe, wird NICHTS angenommen. */
const isFirma = (c: NonNullable<AnyContact>): boolean => {
  const raw =
    typeof c.type === "string" ? c.type : (c.type?.type ?? c.typ ?? "");
  return String(raw).trim().toLowerCase() === "firma";
};

export const displayContactName = (c: AnyContact): string | null => {
  if (!c) return null;
  const trim = (s: unknown) => (typeof s === "string" ? s.trim() : "");
  const direct = trim(c.Anzeigename) || trim(c.displayName);
  if (direct) return direct;
  const firma = trim(c.firmenname) || trim(c.Firmenname) || trim(c.companyName);
  if (firma) return firma;
  const first = trim(c.vorname) || trim(c.Vorname) || trim(c.firstName);
  const last = trim(c.nachname) || trim(c.Nachname) || trim(c.lastname) || trim(c.lastName);
  // Eine Firma trägt ihren Namen allein im Nachnamen-Feld. Steht dort trotzdem
  // ein Vorname, ist er Altbestand aus der Datenübernahme — im PROD-Bestand etwa
  // ein einzelner Punkt bei „Courté J. & Co. KG". Für Firmen wird deshalb nie
  // zusammengesetzt. Fehlt die Typangabe, bleibt es beim bisherigen Verhalten.
  //
  // Der Namenszusatz setzt den Firmennamen fort und hängt mit LEERZEICHEN an, nie
  // mit Komma. Er wird NUR hier angehängt, nicht am `firma`-Zweig oben: dessen
  // Felder (`companyName` & Co.) liefert das Backend bereits fertig zusammen-
  // gesetzt — dort würde der Zusatz ein zweites Mal erscheinen.
  if (isFirma(c)) {
    const zusatz = trim(c.suffix);
    const basis = last || first || trim(c.name);
    return [basis, zusatz].filter(Boolean).join(" ") || null;
  }
  const full = [first, last].filter(Boolean).join(" ").trim();
  if (full) return full;
  return trim(c.name) || null;
};

/* ------------------------------------------------------------------ *
 * Verstorben
 * ------------------------------------------------------------------ */

/**
 * Was ein Aufrufer mitliefern muss, damit der Tod erkennbar ist. Wie oben
 * absichtlich locker: Die Kontaktliste, die Akte und die Suchergebnisse haben
 * verschiedene Shapes.
 */
type MaybeVerstorben =
  | { deceased?: boolean | null; deceasedAt?: string | Date | null }
  | null
  | undefined;

/**
 * Ist der Kontakt verstorben?
 *
 * `deceased` ist die führende Angabe; ein gesetztes `deceasedAt` zählt
 * ebenfalls. Der zweite Teil ist kein Gürtel-und-Hosenträger, sondern nötig:
 * Aufrufer, die nur das Datum laden (oder Datensätze aus der Zeit vor dem
 * Flag), sollen trotzdem richtig angezeigt werden.
 */
export const istVerstorben = (c: MaybeVerstorben): boolean => {
  if (!c) return false;
  if (c.deceased === true) return true;
  const d = c.deceasedAt;
  return d instanceof Date ? true : typeof d === "string" && d.trim() !== "";
};

/** Das Zeichen, das vor dem Namen steht. */
export const VERSTORBEN_ZEICHEN = "†";

/**
 * Der Namenszusatz für die ANZEIGE — bewusst kein Teil von
 * `displayContactName`.
 *
 * Dessen Ergebnis wird an vielen Stellen weiterverwendet, wo es kein Schmuck,
 * sondern ein WERT ist: als `bevollmaechtigterName` beim Speichern, als
 * Suchtext, als Betreff. Ein dort eingebautes Kreuz wanderte in die Datenbank
 * und in Anschreiben. Deshalb liefert diese Funktion nur das Präfix, und jede
 * Anzeigestelle setzt es selbst davor.
 */
export const verstorbenPraefix = (c: MaybeVerstorben): string =>
  istVerstorben(c) ? `${VERSTORBEN_ZEICHEN} ` : "";

/**
 * Anzeigename mit Kreuz, wenn die Person verstorben ist.
 *
 * Für Listen und Überschriften — überall dort, wo der String nur gezeigt und
 * nicht gespeichert wird.
 */
export const displayContactNameMitStatus = (
  c: AnyContact & MaybeVerstorben,
): string | null => {
  const name = displayContactName(c);
  return name === null ? null : `${verstorbenPraefix(c)}${name}`;
};

/** Titel-Attribut für die Anzeige — nennt das Datum, wenn es bekannt ist. */
export const verstorbenTitel = (c: MaybeVerstorben): string | undefined => {
  if (!istVerstorben(c)) return undefined;
  const d = c?.deceasedAt;
  const roh = d instanceof Date ? d.toISOString().slice(0, 10) : String(d ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(roh)) return "Verstorben";
  const [j, m, t] = roh.split("-");
  return `Verstorben am ${t}.${m}.${j}`;
};
